// Black Friday Artist Kickstart: campaign configuration.
// Every commercial value that still needs owner sign-off lives here so it can be
// changed in one place before launch. Defaults are deliberately safe/off.

export const OFFER_VERSION = "bf-2026-v5";

/** Keep the campaign out of search engines while drafting. Flip to false at launch. */
export const BLACK_FRIDAY_NOINDEX = true;

/** Approved campaign name: "The Zero To 1,000 Fans Artist Kickstart". */
export const CAMPAIGN_NAME = "The Zero To 1,000 Fans Artist Kickstart";

/** New Artist Kickstart introduction (Vimeo URL). Empty until filmed: the page shows a quiet compact placeholder. Never reuse the old application video. */
export const INTRO_VIDEO_URL = "";

export const CONTACT_EMAIL = "hello@dk-mba.com";

export type DurationId = "30" | "60" | "90";

export interface DurationOption {
  id: DurationId;
  days: number;
  /** Black Friday price, paid once upfront. */
  priceUsd: number;
  /** Original price before the promotion. */
  originalUsd: number;
  descriptor: string;
  /** Explain the working period when this duration is selected. */
  guidance: string;
}

export const DURATIONS: DurationOption[] = [
  {
    id: "30",
    days: 30,
    priceUsd: 995,
    originalUsd: 1499,
    descriptor: "A focused kickstart",
    guidance: "Get your release plan in place and start putting it into action with us.",
  },
  {
    id: "60",
    days: 60,
    priceUsd: 1849,
    originalUsd: 2499,
    descriptor: "Build on what works",
    guidance: "Stay with us through more rounds of content and feedback, developing what connects.",
  },
  {
    id: "90",
    days: 90,
    priceUsd: 2699,
    originalUsd: 3499,
    descriptor: "Scale the build",
    guidance: "Keep the team alongside you as you develop your fanbase and work through your next release decisions.",
  },
];

export const DEFAULT_DURATION: DurationId = "60";

/**
 * Owner-supplied one-time Stripe payment links, mapped to each kickstart duration.
 */
export const CHECKOUT_LINKS: Record<DurationId, string> = {
  "30": "https://buy.stripe.com/dRm00l3Jc10dawy9zmd7q2Y",
  "60": "https://buy.stripe.com/4gMdRbdjMcIV5ce8vid7q2Z",
  "90": "https://buy.stripe.com/00w14pcfI4cp5ce5j6d7q30",
};

export const SELECTION_STORAGE_KEY = "dk-bf-kickstart-duration";

export function formatUsd(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}

export function getDuration(id: DurationId): DurationOption {
  const found = DURATIONS.find((d) => d.id === id);
  if (!found) throw new Error(`Unknown duration ${id}`);
  return found;
}

export function getSavings(d: DurationOption): number {
  return d.originalUsd - d.priceUsd;
}

export function getSavingsPercent(d: DurationOption): number {
  return Math.round((getSavings(d) / d.originalUsd) * 1000) / 10;
}

export function isDurationId(value: unknown): value is DurationId {
  return value === "30" || value === "60" || value === "90";
}

/** Checkout URL for a duration, or null when no valid https link is configured. */
export function getCheckoutUrl(id: DurationId, links: Record<DurationId, string> = CHECKOUT_LINKS): string | null {
  const url = links[id]?.trim();
  if (!url || !/^https:\/\//.test(url)) return null;
  return url;
}

export const LOWEST_PRICE = Math.min(...DURATIONS.map((d) => d.priceUsd));

/** Restore a deliberate earlier choice; otherwise use the initial default. */
export function readStoredSelection(storage: Pick<Storage, "getItem"> | undefined): DurationId {
  try {
    const v = storage?.getItem(SELECTION_STORAGE_KEY);
    return isDurationId(v) ? v : DEFAULT_DURATION;
  } catch {
    return DEFAULT_DURATION;
  }
}

export function storeSelection(storage: Pick<Storage, "setItem"> | undefined, id: DurationId) {
  try {
    storage?.setItem(SELECTION_STORAGE_KEY, id);
  } catch {
    /* storage unavailable: selection still works for this visit */
  }
}
